import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { exportRowsToCSV } from "@/lib/csvExport";

import { toLocalISODate } from "@/lib/dateUtils";

const money = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const iso = toLocalISODate;

const presets = () => {
  const now = new Date();
  const startOfWeek = new Date(now); startOfWeek.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  return {
    week: [iso(startOfWeek), iso(now)],
    month: [iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(now)],
    year: [iso(new Date(now.getFullYear(), 0, 1)), iso(now)],
  };
};

const GarageExpenseStatement = ({ open, onOpenChange, expenses = [] }) => {
  const p = presets();
  const [range, setRange] = useState(p.month);
  const [active, setActive] = useState("month");

  const applyPreset = (key) => { const pr = presets(); setActive(key); setRange(pr[key]); };

  const { rows, total, byCategory, byMethod } = useMemo(() => {
    const [s, e] = range;
    const inRange = (d) => {
      if (!d) return false;
      const day = String(d).slice(0, 10);
      return day >= s && day <= e;
    };
    const filtered = (expenses || [])
      .filter((x) => inRange(x.date))
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));
    const total = filtered.reduce((sum, x) => sum + (Number(x.amount) || 0), 0);
    const catMap = {};
    const methMap = {};
    filtered.forEach((x) => {
      const c = x.category || "other";
      catMap[c] = catMap[c] || { category: c, total: 0, count: 0 };
      catMap[c].total += Number(x.amount) || 0;
      catMap[c].count += 1;
      const m = x.payment_method || "cash";
      methMap[m] = (methMap[m] || 0) + (Number(x.amount) || 0);
    });
    return {
      rows: filtered,
      total,
      byCategory: Object.values(catMap).sort((a, b) => b.total - a.total),
      byMethod: Object.entries(methMap).map(([method, amt]) => ({ method, amt })),
    };
  }, [expenses, range]);

  const handleExportCSV = () => {
    const headers = ['Date', 'Category', 'Description', 'Vendor', 'Payment Method', 'Amount'];
    const csvRows = rows.map((r) => [
      r.date || '', (r.category || 'other').replace(/_/g, ' '), r.title || r.description || '',
      r.vendor || '', (r.payment_method || 'cash').replace(/_/g, ' '), r.amount || 0,
    ]);
    csvRows.push(['', '', '', '', 'TOTAL', total]);
    exportRowsToCSV(`Garage_Expenses_${range[0]}_to_${range[1]}`, headers, csvRows);
  };

  const printStatement = () => {
    const rowsHtml = rows.map((r) => `
      <tr>
        <td style="border:1px solid #ddd;padding:5px 8px">${r.date || ""}</td>
        <td style="border:1px solid #ddd;padding:5px 8px;text-transform:capitalize">${(r.category || "other").replace(/_/g, " ")}</td>
        <td style="border:1px solid #ddd;padding:5px 8px">${r.title || r.description || ""}</td>
        <td style="border:1px solid #ddd;padding:5px 8px">${r.vendor || ""}</td>
        <td style="border:1px solid #ddd;padding:5px 8px;text-transform:capitalize">${(r.payment_method || "cash").replace(/_/g, " ")}</td>
        <td style="border:1px solid #ddd;padding:5px 8px;text-align:right">${money(r.amount)}</td>
      </tr>`).join("");
    const catHtml = byCategory.map((c) => `
      <tr>
        <td style="border:1px solid #ddd;padding:4px 8px;text-transform:capitalize">${(c.category || "other").replace(/_/g, " ")}</td>
        <td style="border:1px solid #ddd;padding:4px 8px;text-align:right">${c.count}</td>
        <td style="border:1px solid #ddd;padding:4px 8px;text-align:right">${money(c.total)}</td>
      </tr>`).join("");
    const html = `<!doctype html><html><head><title>Garage Expenses Statement</title>
      <style>body{font-family:Arial;padding:24px;color:#111}table{border-collapse:collapse;width:100%;font-size:13px;margin-top:8px}
      h1{font-size:18px;margin:0}h2{font-size:14px;margin:18px 0 4px}.muted{color:#666;font-size:12px}
      th{background:#f3f4f6;border:1px solid #ddd;padding:6px 8px;text-align:left}
      .tot{font-weight:bold;background:#eef}</style></head><body>
      <h1>GARAGE — Garage Expenses Statement</h1>
      <div class="muted">${range[0]} to ${range[1]} &nbsp;•&nbsp; ${rows.length} entr${rows.length === 1 ? "y" : "ies"}</div>
      <table>
        <tr><th>Date</th><th>Category</th><th>Description</th><th>Vendor</th><th>Payment</th><th style="text-align:right">Amount</th></tr>
        ${rowsHtml || `<tr><td colspan="6" style="border:1px solid #ddd;padding:10px;text-align:center;color:#888">No expenses in this period</td></tr>`}
        <tr class="tot"><td colspan="5" style="border:1px solid #ddd;padding:8px;text-align:right">TOTAL</td><td style="border:1px solid #ddd;padding:8px;text-align:right">${money(total)}</td></tr>
      </table>
      <h2>Category Summary</h2>
      <table>
        <tr><th>Category</th><th style="text-align:right">Count</th><th style="text-align:right">Amount</th></tr>
        ${catHtml || `<tr><td colspan="3" style="border:1px solid #ddd;padding:10px;text-align:center;color:#888">—</td></tr>`}
      </table>
      </body></html>`;
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) { toast.error("Allow pop-ups to print"); return; }
    w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="expense-statement-dialog">
        <DialogHeader>
          <DialogTitle>Garage Expenses Statement</DialogTitle>
          <DialogDescription>Full expense statement for a chosen date range, with category summary and print.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-2">
          {[["week", "This Week"], ["month", "This Month"], ["year", "This Year"]].map(([k, l]) => (
            <Button key={k} size="sm" variant={active === k ? "default" : "outline"} onClick={() => applyPreset(k)} data-testid={`expstmt-preset-${k}`}>{l}</Button>
          ))}
          <div className="flex items-end gap-1 ml-2">
            <div><Label className="text-xs">From</Label><Input type="date" className="h-8 w-36" value={range[0]} onChange={(e) => { setActive("custom"); setRange([e.target.value, range[1]]); }} data-testid="expstmt-start" /></div>
            <div><Label className="text-xs">To</Label><Input type="date" className="h-8 w-36" value={range[1]} onChange={(e) => { setActive("custom"); setRange([range[0], e.target.value]); }} data-testid="expstmt-end" /></div>
          </div>
          <Button size="sm" onClick={printStatement} data-testid="expstmt-print">Print / PDF</Button>
          <Button size="sm" variant="outline" onClick={handleExportCSV} data-testid="expstmt-export-csv">Export CSV</Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div className="rounded-md border p-3 text-center"><div className="text-xs text-gray-500">Total Expenses</div><div className="text-lg font-bold text-red-600" data-testid="expstmt-total">{money(total)}</div></div>
          <div className="rounded-md border p-3 text-center"><div className="text-xs text-gray-500">Entries</div><div className="text-lg font-bold" data-testid="expstmt-count">{rows.length}</div></div>
          <div className="rounded-md border p-3 text-center"><div className="text-xs text-gray-500">Categories</div><div className="text-lg font-bold">{byCategory.length}</div></div>
        </div>

        {byCategory.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="expstmt-category-summary">
            {byCategory.map((c) => (
              <span key={c.category} className="text-xs bg-gray-100 rounded-full px-3 py-1 capitalize">
                {(c.category || "other").replace(/_/g, " ")}: <b>{money(c.total)}</b> ({c.count})
              </span>
            ))}
          </div>
        )}

        <div className="border rounded-md overflow-hidden">
          <div className="grid grid-cols-12 bg-gray-50 px-3 py-2 text-xs font-semibold border-b">
            <span className="col-span-2">Date</span>
            <span className="col-span-2">Category</span>
            <span className="col-span-4">Description</span>
            <span className="col-span-2">Payment</span>
            <span className="col-span-2 text-right">Amount</span>
          </div>
          <div className="max-h-[42vh] overflow-y-auto" data-testid="expstmt-rows">
            {rows.length === 0 ? (
              <p className="text-sm text-gray-400 px-3 py-6 text-center">No expenses in this period</p>
            ) : rows.map((r, i) => (
              <div key={r.id || i} className="grid grid-cols-12 px-3 py-1.5 text-xs border-t items-center" data-testid={`expstmt-row-${i}`}>
                <span className="col-span-2 text-gray-500">{r.date}</span>
                <span className="col-span-2 capitalize">{(r.category || "other").replace(/_/g, " ")}</span>
                <span className="col-span-4 truncate" title={r.title || r.description}>
                  {r.title || r.description || "Expense"}
                  {r.vendor ? <span className="text-gray-400"> • {r.vendor}</span> : null}
                </span>
                <span className="col-span-2 capitalize text-gray-600">{(r.payment_method || "cash").replace(/_/g, " ")}</span>
                <span className="col-span-2 text-right font-medium">{money(r.amount)}</span>
              </div>
            ))}
          </div>
          {rows.length > 0 && (
            <div className="grid grid-cols-12 px-3 py-2 border-t bg-gray-50 text-sm font-bold">
              <span className="col-span-10 text-right">TOTAL</span>
              <span className="col-span-2 text-right text-red-600">{money(total)}</span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GarageExpenseStatement;
